import { TextLink } from '@ovotech/nebula';
import { Sandwich } from '@ovotech/ui-tools';
import React from 'react';
import MovingHomeLink from '../Links/MovingHomeLink';
import NavLink from '../Links/NavLink';
import { useContractsQueryLegacy, useRenewalEligibilityQuery } from '@/src/api';
import AnalyticsWrapper from '@/src/components/AnalyticsWrapper/AnalyticsWrapper';
import { StyledNavLink } from '@/src/components/Header/Header.styled';

import {
  CLICKED_USAGE_IN_NAV,
  MENU,
  SMB_NAV_BOOKING_CLICKED,
  SMB_NAV_BOOKING_VIEWED,
} from '@/src/constants/analytics';
import { SMART_BOOKING_URL } from '@/src/constants/endpoints';
import {
  BEYOND_WEB_PRESENCE,
  CHARGE_ANYTIME_ENABLE_MANAGE_PLAN,
} from '@/src/constants/feature-flags';
import {
  ROUTE_ANYTIME,
  ROUTE_BILLING_HISTORY,
  ROUTE_HOME,
  ROUTE_METER_READINGS,
  ROUTE_OFFERS,
  ROUTE_PAYG_TOPUP_HISTORY,
  ROUTE_PAYMENTS,
  ROUTE_PLAN,
  ROUTE_RENEWAL,
  ROUTE_USAGE,
} from '@/src/constants/routes';
import { sandwichIds } from '@/src/constants/sandwich-ids';
import {
  hasActiveContracts,
  hasFutureContracts,
} from '@/src/pages/Renewal/selectors';
import {
  OptimizelyExperiment,
  OptimizelyVariation,
  useFeature,
} from '@/src/utils/optimizely';
import RewardsLink from '../Rewards/RewardsLink';
import {
  PaygOnly,
  PaymOnly,
} from '@/src/components/BillingTypeGuards/BillingTypeGuards';
import { HELP_OVO_ENERGY_PAY_AS_YOU_GO } from '@/src/constants/links';

type Props = {
  hasGreenerEnergy: boolean;
  path: string;
  accountId: string;
  smartEligible: boolean;
  hasSmartMeterBooking: boolean | undefined;
};

const InLifeSideNavigationLinks = ({
  path,
  hasGreenerEnergy,
  accountId,
  smartEligible,
  hasSmartMeterBooking,
}: Props) => {
  const contractQuery = useContractsQueryLegacy(accountId);
  const renewalEligibilityQuery = useRenewalEligibilityQuery(accountId);
  const [beyondWebPresenceEnabled] = useFeature(BEYOND_WEB_PRESENCE);

  const [enableChargeAnytimeManagePlan] = useFeature(
    CHARGE_ANYTIME_ENABLE_MANAGE_PLAN,
  );

  const hasSomeActiveContract =
    contractQuery.status === 'success' &&
    hasActiveContracts(contractQuery.data);

  const hasSomeFutureContract =
    contractQuery.status === 'success' &&
    hasFutureContracts(contractQuery.data);

  const canRenew = renewalEligibilityQuery.data?.eligible === true;

  return (
    <ul>
      <Sandwich id={sandwichIds.sideNavigation.home}>
        <li>
          <NavLink path={path} route={ROUTE_HOME} label="Home" />
        </li>
      </Sandwich>
      <Sandwich id={sandwichIds.sideNavigation.usage}>
        <li>
          <NavLink
            data-event-name={CLICKED_USAGE_IN_NAV}
            data-testid="usage-link"
            path={path}
            route={ROUTE_USAGE}
            label="Usage"
          />
        </li>
      </Sandwich>
      <Sandwich id={sandwichIds.sideNavigation.getSmartMeter}>
        {/* @ts-ignore react-18 children props */}
        {smartEligible && !hasSmartMeterBooking && (
          <OptimizelyExperiment experiment="smart_book_menu">
            <OptimizelyVariation variation="original"></OptimizelyVariation>
            <OptimizelyVariation variation="booking_in_side_nav">
              <li>
                <AnalyticsWrapper name={SMB_NAV_BOOKING_VIEWED}>
                  <StyledNavLink
                    as={TextLink}
                    href={`${SMART_BOOKING_URL}?accountId=${accountId}&utm_source=menu&utm_medium=MyOVO&utm_campaign=smart-meter-booking`}
                    opensInNewWindow={false}
                    data-testid="smb-side-nav"
                    data-event-name={SMB_NAV_BOOKING_CLICKED}
                  >
                    Get a smart meter
                  </StyledNavLink>
                </AnalyticsWrapper>
              </li>
            </OptimizelyVariation>
          </OptimizelyExperiment>
        )}
      </Sandwich>
      <Sandwich id={sandwichIds.sideNavigation.meterReadings} props={{ path }}>
        <li>
          <NavLink
            data-event-name={MENU.METER_READINGS}
            path={path}
            route={`${ROUTE_METER_READINGS}`}
            label="Meter readings"
            data-testid="meter-readings-link"
          />
        </li>
      </Sandwich>
      <PaygOnly>
        <Sandwich id={sandwichIds.sideNavigation.topupHistory} props={{ path }}>
          <li>
            <NavLink
              data-event-name={MENU.TOPUP_HISTORY}
              path={path}
              route={ROUTE_PAYG_TOPUP_HISTORY}
              label="Top-up history"
              data-testid="topup-history-link"
            />
          </li>
        </Sandwich>
      </PaygOnly>
      <PaymOnly>
        <Sandwich id={sandwichIds.sideNavigation.billing}>
          <li>
            <NavLink
              path={path}
              route={`${ROUTE_BILLING_HISTORY}`}
              label="Billing history"
              data-testid="billing-history-link"
              data-event-name={MENU.BILLING_HISTORY}
            />
          </li>
        </Sandwich>
      </PaymOnly>
      <PaymOnly>
        <Sandwich id={sandwichIds.sideNavigation.payments}>
          <li>
            <NavLink
              data-event-name={MENU.PAYMENTS}
              path={path}
              route={ROUTE_PAYMENTS}
              label="Payments"
            />
          </li>
        </Sandwich>
      </PaymOnly>
      <Sandwich
        id={sandwichIds.sideNavigation.plan}
        props={{ path, hasGreenerEnergy, accountId }}
      >
        <li>
          <NavLink
            data-event-name={MENU.PLAN_DETAILS}
            path={path}
            route={ROUTE_PLAN}
            label="Plan"
            data-testid="plan-link"
          />
        </li>
      </Sandwich>

      <PaymOnly>
        <Sandwich id={sandwichIds.sideNavigation.offers}>
          <li>
            <NavLink
              data-event-name={MENU.OFFERS}
              path={path}
              route={ROUTE_OFFERS}
              label="Offers"
            />
          </li>
        </Sandwich>
      </PaymOnly>

      <PaymOnly>
        {enableChargeAnytimeManagePlan && (
          <Sandwich id={sandwichIds.sideNavigation.chargeAnytime}>
            <li>
              <NavLink
                data-event-name={MENU.CHARGE_ANYTIME}
                path={path}
                route={ROUTE_ANYTIME}
                label="Charge Anytime"
              />
            </li>
          </Sandwich>
        )}
      </PaymOnly>

      <PaymOnly>
        {beyondWebPresenceEnabled && (
          <Sandwich id={sandwichIds.sideNavigation.rewards}>
            <li>
              <RewardsLink type="accordion" path={path} />
            </li>
          </Sandwich>
        )}
      </PaymOnly>

      <PaymOnly>
        {(hasSomeActiveContract || hasSomeFutureContract) && (
          <Sandwich id={sandwichIds.sideNavigation.movingHome}>
            <li>
              <MovingHomeLink path={path} />
            </li>
          </Sandwich>
        )}
      </PaymOnly>

      {canRenew && (
        <Sandwich id={sandwichIds.sideNavigation.renewPlan}>
          <li>
            <NavLink
              path={path}
              route={`${ROUTE_RENEWAL}?utm_source=renew-your-plan&utm_medium=MyOVO`}
              label="Renew your plan"
              data-testid="renew-link"
            />
          </li>
        </Sandwich>
      )}
      <PaygOnly>
        <Sandwich id={sandwichIds.sideNavigation.paygHelp}>
          <li>
            <StyledNavLink
              as={TextLink}
              href={HELP_OVO_ENERGY_PAY_AS_YOU_GO}
              opensInNewWindow
            >
              Help
            </StyledNavLink>
          </li>
        </Sandwich>
      </PaygOnly>
    </ul>
  );
};

const SideNavigationLinks = (props: Props) => (
  <InLifeSideNavigationLinks {...props} />
);

export default SideNavigationLinks;
